import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { Chip } from 'primereact/chip';
import { Card } from 'primereact/card';
import { ProgressBar } from 'primereact/progressbar';
import { Badge } from 'primereact/badge';
import { Panel } from 'primereact/panel';
import { TabView, TabPanel } from 'primereact/tabview';
import { Chart } from 'primereact/chart';
import { FileUpload } from 'primereact/fileupload';
import { FilterMatchMode } from 'primereact/api';
import axiosInstance from '../config/axios.config';

// Helper functions
const getLenderStatusSeverity = (status) => {
    const severityMap = {
        'active': 'success',
        'inactive': 'secondary',
        'suspended': 'danger',
        'under_review': 'warning',
        'terminated': 'danger'
    };
    return severityMap[status] || 'info';
};

const getPerformanceGradeSeverity = (grade) => {
    if (['A+', 'A'].includes(grade)) return 'success';
    if (['B+', 'B'].includes(grade)) return 'warning';
    if (['C+', 'C'].includes(grade)) return 'info';
    return 'danger';
};

const Lenders = () => {
    const [lenders, setLenders] = useState([]);
    const [selectedLender, setSelectedLender] = useState(null);
    const [lenderDialog, setLenderDialog] = useState(false);
    const [lenderDetailDialog, setLenderDetailDialog] = useState(false);
    const [performanceDialog, setPerformanceDialog] = useState(false);
    const [newLenderDialog, setNewLenderDialog] = useState(false);
    const [loading, setLoading] = useState(true);
    const [globalFilter, setGlobalFilter] = useState('');
    const [statistics, setStatistics] = useState({
        total_lenders: 0,
        active_lenders: 0,
        avg_approval_rate: 0,
        avg_disbursement_time: 0
    });
    const [newLender, setNewLender] = useState({
        legal_name: '',
        brand_name: '',
        lender_type: 'bank',
        lender_status: 'under_review',
        contact_email: '',
        contact_phone: '',
        head_office_address: '',
        accepts_new_applications: true,
        api_endpoint: '',
        integration_status: 'not_configured'
    });
    const [performanceData, setPerformanceData] = useState(null);
    
    // Rate Management State (NEW)
    const [uploadDialog, setUploadDialog] = useState(false);
    const [uploadType, setUploadType] = useState('category');
    const [selectedLenderForUpload, setSelectedLenderForUpload] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    
    const toast = useRef(null);
    const dt = useRef(null);

    const [filters, setFilters] = useState({
        'global': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'brand_name': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'lender_type': { value: null, matchMode: FilterMatchMode.EQUALS },
        'lender_status': { value: null, matchMode: FilterMatchMode.EQUALS },
    });

    const lenderTypeOptions = [
        { label: 'Bank', value: 'bank' },
        { label: 'NBFC', value: 'nbfc' },
        { label: 'Fintech', value: 'fintech' },
        { label: 'Credit Union', value: 'credit_union' },
        { label: 'Cooperative Bank', value: 'cooperative_bank' },
    ];

    const lenderStatusOptions = [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'Suspended', value: 'suspended' },
        { label: 'Under Review', value: 'under_review' },
        { label: 'Terminated', value: 'terminated' },
    ];

    useEffect(() => {
        loadLenders();
        loadStatistics();
    }, []);

    const loadLenders = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/lenders');
            if (response.data.success) {
                const lenderData = response.data.data;
                // Ensure data is always an array
                if (Array.isArray(lenderData)) {
                    setLenders(lenderData);
                } else if (lenderData && Array.isArray(lenderData.data)) {
                    setLenders(lenderData.data);
                } else {
                    setLenders([]);
                }
            } else {
                setLenders([]);
            }
        } catch (error) {
            console.error('Error loading lenders:', error);
            setLenders([]);
            
            if (error.response?.status === 401) {
                toast.current.show({ 
                    severity: 'warn', 
                    summary: 'Authentication Required', 
                    detail: 'Please log in to view lenders',
                    life: 3000 
                });
            } else if (error.response?.status === 500) {
                toast.current.show({ 
                    severity: 'error', 
                    summary: 'Server Error', 
                    detail: 'Lender management system may need configuration. Contact administrator.',
                    life: 4000 
                });
            } else {
                toast.current.show({ 
                    severity: 'info', 
                    summary: 'Info', 
                    detail: 'Lender data will be available once authenticated.',
                    life: 3000 
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const loadStatistics = async () => {
        try {
            const response = await axiosInstance.get('/lenders/statistics');
            if (response.data.success) {
                setStatistics(response.data.data);
            }
        } catch (error) {
            console.error('Error loading statistics:', error);
            // Keep the initialized statistics object
        }
    };

    const openNewLender = () => {
        setNewLender({
            legal_name: '',
            brand_name: '',
            lender_type: 'bank',
            lender_status: 'under_review',
            contact_email: '',
            contact_phone: '',
            head_office_address: '',
            accepts_new_applications: true,
            api_endpoint: '',
            integration_status: 'not_configured'
        });
        setNewLenderDialog(true);
    };

    const viewLenderDetails = (lender) => {
        setSelectedLender(lender);
        setLenderDetailDialog(true);
    };

    const viewPerformance = async (lender) => {
        setSelectedLender(lender);
        try {
            const response = await axiosInstance.get(`/lenders/${lender.id}/performance`);
            if (response.data.success) {
                setPerformanceData(response.data.data);
            }
        } catch (error) {
            console.error('Error loading performance data:', error);
            setPerformanceData(null);
        }
        setPerformanceDialog(true);
    };

    // CSV Upload Functions (NEW)
    const handleCSVUpload = async (event) => {
        const file = event.files[0];
        if (!file) return;

        try {
            setUploadProgress(0);
            
            const formData = new FormData();
            formData.append('file', file);
            
            let uploadUrl = '';
            
            if (uploadType === 'category' && selectedLenderForUpload) {
                formData.append('bank_id', selectedLenderForUpload.id);
                formData.append('upload_type', uploadType);
                uploadUrl = '/bank-rates/upload-csv';
            } else if (uploadType === 'company') {
                uploadUrl = '/company-categories/import-companies';
            } else {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Missing Information',
                    detail: 'Please select upload type and lender (if required)'
                });
                return;
            }
            
            const response = await axiosInstance.post(uploadUrl, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    setUploadProgress(progress);
                }
            });
            
            if (response.data.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Upload Complete',
                    detail: `Processed ${response.data.data.processed || 0} records successfully`
                });
                
                setUploadDialog(false);
                setUploadProgress(0);
            }
        } catch (error) {
            console.error('Upload failed:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Upload Failed',
                detail: error.response?.data?.message || 'Failed to upload CSV'
            });
        }
    };

    const openUploadDialog = (lender = null) => {
        setSelectedLenderForUpload(lender);
        setUploadType(lender ? 'category' : 'company');
        setUploadDialog(true);
    };

    const handleDeleteLender = async (lender) => {
        if (!lender || !lender.id) return;
        const name = lender.brand_name || lender.legal_name || 'this lender';
        const confirmed = window.confirm(`Are you sure you want to delete ${name}?`);
        if (!confirmed) return;

        try {
            const response = await axiosInstance.delete(`/lenders/${lender.id}`);
            if (response.data?.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Lender deleted successfully',
                    life: 3000,
                });
                await loadLenders();
                await loadStatistics();
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data?.message || 'Failed to delete lender',
                    life: 4000,
                });
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to delete lender',
                life: 4000,
            });
        }
    };

    const lenderTypeBodyTemplate = (rowData) => {
        return <Chip label={rowData.lender_type?.toUpperCase()} className="text-sm" />;
    };

    const statusBodyTemplate = (rowData) => {
        return <Tag value={rowData.lender_status} severity={getLenderStatusSeverity(rowData.lender_status)} />;
    };

    const performanceBodyTemplate = (rowData) => {
        const grade = rowData.performance_grade || 'C';
        return (
            <div className="flex align-items-center gap-2">
                <Tag value={grade} severity={getPerformanceGradeSeverity(grade)} />
                <span className="text-sm">{rowData.approval_rate_percentage}%</span>
            </div>
        );
    };

    const apiStatusBodyTemplate = (rowData) => {
        const isActive = rowData.integration_status === 'active';
        return isActive ? 
            <Tag value="API Active" severity="success" icon="pi pi-check" /> : 
            <Tag value="No API" severity="secondary" />;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <Button 
                    icon="pi pi-eye" 
                    className="p-button-rounded p-button-info p-button-text" 
                    onClick={() => viewLenderDetails(rowData)}
                    tooltip="View Details"
                />
                <Button 
                    icon="pi pi-chart-line" 
                    className="p-button-rounded p-button-success p-button-text" 
                    onClick={() => viewPerformance(rowData)}
                    tooltip="Performance"
                />
                <Button 
                    icon="pi pi-cog" 
                    className="p-button-rounded p-button-warning p-button-text" 
                    onClick={() => configureLender(rowData)}
                    tooltip="Configure"
                />
                <Button 
                    icon="pi pi-trash" 
                    className="p-button-rounded p-button-danger p-button-text" 
                    onClick={() => handleDeleteLender(rowData)}
                    tooltip="Delete"
                />
            </div>
        );
    };

    const configureLender = (lender) => {
        setSelectedLender({...lender}); // Create a copy for editing
        setLenderDialog(true);
    };

    const updateLender = async () => {
        try {
            if (!selectedLender || !selectedLender.id) {
                toast.current.show({ 
                    severity: 'error', 
                    summary: 'Error', 
                    detail: 'No lender selected for update',
                    life: 3000 
                });
                return;
            }

            console.log('Updating lender with data:', selectedLender);
            const response = await axiosInstance.put(`/lenders/${selectedLender.id}`, selectedLender);
            
            if (response.data.success) {
                toast.current.show({ 
                    severity: 'success', 
                    summary: 'Success', 
                    detail: 'Lender updated successfully', 
                    life: 3000 
                });
                setLenderDialog(false);
                loadLenders();
                loadStatistics();
                setSelectedLender(null);
            }
        } catch (error) {
            console.error('Error updating lender:', error);
            
            let errorMessage = 'Failed to update lender';
            
            if (error.response?.status === 400) {
                const validationErrors = error.response?.data?.errors;
                if (validationErrors) {
                    const errorMessages = Object.values(validationErrors).flat();
                    errorMessage = 'Validation errors: ' + errorMessages.join(', ');
                } else {
                    errorMessage = error.response?.data?.message || 'Invalid input data';
                }
            } else if (error.response?.status === 401) {
                errorMessage = 'Please log in to update lenders';
            } else {
                errorMessage = error.response?.data?.message || 'Failed to update lender';
            }
            
            toast.current.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: errorMessage, 
                life: 5000 
            });
        }
    };

    const createNewLender = async () => {
        try {
            // Basic validation
            if (!newLender.legal_name || !newLender.head_office_address) {
                toast.current.show({ 
                    severity: 'error', 
                    summary: 'Validation Error', 
                    detail: 'Please provide at least legal name and head office address',
                    life: 3000 
                });
                return;
            }

            console.log('Creating lender with data:', newLender);
            const response = await axiosInstance.post('/lenders', newLender);
            
            if (response.data.success) {
                toast.current.show({ 
                    severity: 'success', 
                    summary: 'Success', 
                    detail: 'Lender created successfully', 
                    life: 3000 
                });
                setNewLenderDialog(false);
                loadLenders();
                loadStatistics();
                
                // Reset form
                setNewLender({
                    legal_name: '',
                    brand_name: '',
                    lender_type: 'bank',
                    lender_status: 'under_review',
                    contact_email: '',
                    contact_phone: '',
                    head_office_address: '',
                    accepts_new_applications: true,
                    api_endpoint: '',
                    integration_status: 'not_configured'
                });
            }
        } catch (error) {
            console.error('Error creating lender:', error);
            console.log('Error details:', {
                status: error.response?.status,
                data: error.response?.data,
                validationErrors: error.response?.data?.errors
            });
            
            let errorMessage = 'Failed to create lender';
            
            if (error.response?.status === 400) {
                const validationErrors = error.response?.data?.errors;
                if (validationErrors) {
                    const errorMessages = Object.values(validationErrors).flat();
                    errorMessage = 'Validation errors: ' + errorMessages.join(', ');
                } else {
                    errorMessage = error.response?.data?.message || 'Invalid input data';
                }
            } else if (error.response?.status === 401) {
                errorMessage = 'Please log in to create lenders';
            } else {
                errorMessage = error.response?.data?.message || 'Failed to create lender';
            }
            
            toast.current.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: errorMessage, 
                life: 5000 
            });
        }
    };

    const leftToolbarTemplate = () => {
        return (
            <div className="flex align-items-center gap-2">
                <Button label="New Lender" icon="pi pi-plus" className="p-button-success" onClick={openNewLender} />
                <Button label="Refresh" icon="pi pi-refresh" className="p-button-help" onClick={loadLenders} />
            </div>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between">
            <h5 className="m-0">Lender Management</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." />
            </span>
        </div>
    );

    return (
        <div className="grid">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    
                    {/* Statistics Cards */}
                    {statistics && (
                        <div className="grid mb-4">
                            <div className="col-12 lg:col-3">
                                <Card className="bg-blue-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Total Lenders</span>
                                            <div className="text-900 font-medium text-xl">{statistics.total_lenders}</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-blue-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-building text-blue-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                            <div className="col-12 lg:col-3">
                                <Card className="bg-green-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Active Lenders</span>
                                            <div className="text-900 font-medium text-xl">{statistics.active_lenders}</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-green-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-check-circle text-green-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                            <div className="col-12 lg:col-3">
                                <Card className="bg-orange-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Avg Approval Rate</span>
                                            <div className="text-900 font-medium text-xl">{statistics.avg_approval_rate}%</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-orange-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-percentage text-orange-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                            <div className="col-12 lg:col-3">
                                <Card className="bg-purple-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Avg Processing</span>
                                            <div className="text-900 font-medium text-xl">{statistics.avg_disbursement_time}h</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-purple-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-clock text-purple-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        </div>
                    )}

                    <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>

                    <DataTable
                        ref={dt}
                        value={lenders}
                        dataKey="id"
                        paginator
                        rows={15}
                        rowsPerPageOptions={[10, 15, 25]}
                        className="p-datatable-gridlines"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} lenders"
                        globalFilter={globalFilter}
                        filters={filters}
                        filterDisplay="menu"
                        loading={loading}
                        responsiveLayout="scroll"
                        header={header}
                    >
                        <Column field="brand_name" header="Lender Name" sortable style={{ minWidth: '14rem' }} body={(rowData) => (
                            <div>
                                <div className="font-semibold">{rowData.brand_name || rowData.legal_name}</div>
                                <div className="text-sm text-500">{rowData.lender_code}</div>
                            </div>
                        )} />
                        <Column field="lender_type" header="Type" body={lenderTypeBodyTemplate} sortable style={{ minWidth: '8rem' }} />
                        <Column field="lender_status" header="Status" body={statusBodyTemplate} sortable style={{ minWidth: '8rem' }} />
                        <Column header="Products" style={{ minWidth: '8rem' }} body={(rowData) => (
                            <Badge value={rowData.active_products_count || 0} severity="info" />
                        )} />
                        <Column header="Performance" body={performanceBodyTemplate} style={{ minWidth: '10rem' }} />
                        <Column header="API Status" body={apiStatusBodyTemplate} style={{ minWidth: '10rem' }} />
                        <Column body={actionBodyTemplate} header="Actions" style={{ minWidth: '12rem' }}></Column>
                    </DataTable>

                    {/* New Lender Dialog */}
                    <Dialog 
                        visible={newLenderDialog} 
                        style={{ width: '800px' }} 
                        header="Create New Lender" 
                        modal 
                        className="p-fluid"
                        onHide={() => setNewLenderDialog(false)}
                        footer={
                            <div>
                                <Button label="Cancel" icon="pi pi-times" className="p-button-text" 
                                    onClick={() => setNewLenderDialog(false)} />
                                <Button label="Create Lender" icon="pi pi-check" className="p-button-text" 
                                    onClick={createNewLender} />
                            </div>
                        }
                    >
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="legal_name">Legal Name *</label>
                                    <InputText 
                                        id="legal_name" 
                                        value={newLender.legal_name || ''} 
                                        onChange={(e) => setNewLender({...newLender, legal_name: e.target.value})} 
                                        placeholder="Enter legal name"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="brand_name">Brand Name</label>
                                    <InputText 
                                        id="brand_name" 
                                        value={newLender.brand_name || ''} 
                                        onChange={(e) => setNewLender({...newLender, brand_name: e.target.value})} 
                                        placeholder="Enter brand name"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="lender_type">Lender Type *</label>
                                    <Dropdown 
                                        id="lender_type"
                                        value={newLender.lender_type} 
                                        options={lenderTypeOptions} 
                                        onChange={(e) => setNewLender({...newLender, lender_type: e.value})} 
                                        placeholder="Select Lender Type"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="lender_status">Status</label>
                                    <Dropdown 
                                        id="lender_status"
                                        value={newLender.lender_status} 
                                        options={lenderStatusOptions} 
                                        onChange={(e) => setNewLender({...newLender, lender_status: e.value})} 
                                        placeholder="Select Status"
                                    />
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="field">
                                    <label htmlFor="head_office_address">Head Office Address *</label>
                                    <InputTextarea 
                                        id="head_office_address" 
                                        value={newLender.head_office_address || ''} 
                                        onChange={(e) => setNewLender({...newLender, head_office_address: e.target.value})} 
                                        rows={3}
                                        placeholder="Enter head office address"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="contact_email">Contact Email</label>
                                    <InputText 
                                        id="contact_email" 
                                        value={newLender.contact_email || ''} 
                                        onChange={(e) => setNewLender({...newLender, contact_email: e.target.value})} 
                                        placeholder="Enter contact email"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="contact_phone">Contact Phone</label>
                                    <InputText 
                                        id="contact_phone" 
                                        value={newLender.contact_phone || ''} 
                                        onChange={(e) => setNewLender({...newLender, contact_phone: e.target.value})} 
                                        placeholder="Enter contact phone"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="api_endpoint">API Endpoint</label>
                                    <InputText 
                                        id="api_endpoint" 
                                        value={newLender.api_endpoint || ''} 
                                        onChange={(e) => setNewLender({...newLender, api_endpoint: e.target.value})} 
                                        placeholder="https://api.lender.com/v1"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="license_number">License Number</label>
                                    <InputText 
                                        id="license_number" 
                                        value={newLender.license_number || ''} 
                                        onChange={(e) => setNewLender({...newLender, license_number: e.target.value})} 
                                        placeholder="Enter license number"
                                    />
                                </div>
                            </div>
                        </div>
                    </Dialog>

                    {/* Edit Lender Dialog */}
                    <Dialog 
                        visible={lenderDialog} 
                        style={{ width: '800px' }} 
                        header={selectedLender ? `Edit Lender: ${selectedLender.brand_name || selectedLender.legal_name}` : 'Edit Lender'} 
                        modal 
                        className="p-fluid"
                        onHide={() => {
                            setLenderDialog(false);
                            setSelectedLender(null);
                        }}
                        footer={
                            <div>
                                <Button label="Cancel" icon="pi pi-times" className="p-button-text" 
                                    onClick={() => {
                                        setLenderDialog(false);
                                        setSelectedLender(null);
                                    }} />
                                <Button label="Update Lender" icon="pi pi-check" className="p-button-text" 
                                    onClick={updateLender} />
                            </div>
                        }
                    >
                        {selectedLender && (
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="edit_legal_name">Legal Name *</label>
                                        <InputText 
                                            id="edit_legal_name" 
                                            value={selectedLender.legal_name || ''} 
                                            onChange={(e) => setSelectedLender({...selectedLender, legal_name: e.target.value})} 
                                            placeholder="Enter legal name"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="edit_brand_name">Brand Name</label>
                                        <InputText 
                                            id="edit_brand_name" 
                                            value={selectedLender.brand_name || ''} 
                                            onChange={(e) => setSelectedLender({...selectedLender, brand_name: e.target.value})} 
                                            placeholder="Enter brand name"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="edit_lender_type">Lender Type *</label>
                                        <Dropdown 
                                            id="edit_lender_type"
                                            value={selectedLender.lender_type} 
                                            options={lenderTypeOptions} 
                                            onChange={(e) => setSelectedLender({...selectedLender, lender_type: e.value})} 
                                            placeholder="Select Lender Type"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="edit_lender_status">Status *</label>
                                        <Dropdown 
                                            id="edit_lender_status"
                                            value={selectedLender.lender_status} 
                                            options={lenderStatusOptions} 
                                            onChange={(e) => setSelectedLender({...selectedLender, lender_status: e.value})} 
                                            placeholder="Select Status"
                                        />
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="field">
                                        <label htmlFor="edit_head_office_address">Head Office Address *</label>
                                        <InputTextarea 
                                            id="edit_head_office_address" 
                                            value={selectedLender.head_office_address || ''} 
                                            onChange={(e) => setSelectedLender({...selectedLender, head_office_address: e.target.value})} 
                                            rows={3}
                                            placeholder="Enter head office address"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="edit_contact_email">Contact Email</label>
                                        <InputText 
                                            id="edit_contact_email" 
                                            value={selectedLender.contact_email || ''} 
                                            onChange={(e) => setSelectedLender({...selectedLender, contact_email: e.target.value})} 
                                            placeholder="Enter contact email"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="edit_contact_phone">Contact Phone</label>
                                        <InputText 
                                            id="edit_contact_phone" 
                                            value={selectedLender.contact_phone || ''} 
                                            onChange={(e) => setSelectedLender({...selectedLender, contact_phone: e.target.value})} 
                                            placeholder="Enter contact phone"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="edit_api_endpoint">API Endpoint</label>
                                        <InputText 
                                            id="edit_api_endpoint" 
                                            value={selectedLender.api_endpoint || ''} 
                                            onChange={(e) => setSelectedLender({...selectedLender, api_endpoint: e.target.value})} 
                                            placeholder="https://api.lender.com/v1"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="edit_license_number">License Number</label>
                                        <InputText 
                                            id="edit_license_number" 
                                            value={selectedLender.license_number || ''} 
                                            onChange={(e) => setSelectedLender({...selectedLender, license_number: e.target.value})} 
                                            placeholder="Enter license number"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="edit_accepts_applications">Accept New Applications</label>
                                        <div className="flex align-items-center mt-2">
                                            <InputSwitch 
                                                id="edit_accepts_applications"
                                                checked={selectedLender.accepts_new_applications || false} 
                                                onChange={(e) => setSelectedLender({...selectedLender, accepts_new_applications: e.value})} 
                                            />
                                            <span className="ml-2">{selectedLender.accepts_new_applications ? 'Yes' : 'No'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="edit_integration_status">Integration Status</label>
                                        <Dropdown 
                                            id="edit_integration_status"
                                            value={selectedLender.integration_status || 'not_configured'} 
                                            options={[
                                                { label: 'Not Configured', value: 'not_configured' },
                                                { label: 'Testing', value: 'testing' },
                                                { label: 'Active', value: 'active' },
                                                { label: 'Suspended', value: 'suspended' }
                                            ]} 
                                            onChange={(e) => setSelectedLender({...selectedLender, integration_status: e.value})} 
                                            placeholder="Select Integration Status"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </Dialog>

                    {/* Lender Detail Dialog */}
                    <Dialog 
                        visible={lenderDetailDialog} 
                        style={{ width: '1200px' }} 
                        header={selectedLender ? `Lender: ${selectedLender.brand_name || selectedLender.legal_name}` : ''} 
                        modal 
                        onHide={() => setLenderDetailDialog(false)}
                        maximizable
                    >
                        {selectedLender && (
                            <TabView>
                                <TabPanel header="Overview">
                                    <LenderOverview lender={selectedLender} />
                                </TabPanel>
                                <TabPanel header="Performance">
                                    <LenderPerformance lender={selectedLender} />
                                </TabPanel>
                                <TabPanel header="Products">
                                    <LenderProducts lender={selectedLender} />
                                </TabPanel>
                                <TabPanel header="API Integration">
                                    <APIIntegration lender={selectedLender} />
                                </TabPanel>
                            </TabView>
                        )}
                    </Dialog>

                    {/* Performance Dialog */}
                    <Dialog 
                        visible={performanceDialog} 
                        style={{ width: '1000px' }} 
                        header={selectedLender ? `Performance: ${selectedLender.brand_name}` : ''} 
                        modal 
                        onHide={() => setPerformanceDialog(false)}
                    >
                        {performanceData && <PerformanceDashboard data={performanceData} />}
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

// Supporting Components
const LenderOverview = ({ lender }) => (
    <div className="grid">
        <div className="col-12 md:col-6">
            <Card title="Lender Information">
                <div className="field">
                    <label>Legal Name:</label>
                    <div className="font-bold">{lender.legal_name}</div>
                </div>
                <div className="field">
                    <label>Brand Name:</label>
                    <div className="font-bold">{lender.brand_name || 'Same as legal name'}</div>
                </div>
                <div className="field">
                    <label>Lender Type:</label>
                    <Chip label={lender.lender_type?.toUpperCase()} />
                </div>
                <div className="field">
                    <label>License Number:</label>
                    <div>{lender.license_number || 'Not provided'}</div>
                </div>
                <div className="field">
                    <label>Contact Email:</label>
                    <div>{lender.contact_email || 'Not provided'}</div>
                </div>
            </Card>
        </div>
        <div className="col-12 md:col-6">
            <Card title="Business Metrics">
                <div className="field">
                    <label>Approval Rate:</label>
                    <div className="flex align-items-center gap-2">
                        <ProgressBar value={lender.approval_rate_percentage || 0} style={{ width: '100px' }}  aria-label="Progress" />
                        <span className="font-bold text-blue-500">{lender.approval_rate_percentage || 0}%</span>
                    </div>
                </div>
                <div className="field">
                    <label>Disbursement TAT:</label>
                    <div className="font-bold">{lender.disbursement_tat_hours || 'N/A'} hours</div>
                </div>
                <div className="field">
                    <label>Default Rate:</label>
                    <div className="font-bold text-red-500">{lender.default_rate_percentage || 0}%</div>
                </div>
                <div className="field">
                    <label>API Uptime:</label>
                    <div className="font-bold text-green-500">{lender.api_uptime_percentage || 0}%</div>
                </div>
            </Card>
        </div>
    </div>
);

const LenderPerformance = ({ lender }) => {
    const performanceScore = lender.calculatePerformanceScore?.() || 75;
    
    return (
        <div className="grid">
            <div className="col-12 md:col-4">
                <Card title="Overall Performance" className="text-center">
                    <div className="text-6xl font-bold text-blue-500">{performanceScore.toFixed(1)}</div>
                    <div className="text-500 mt-2">Performance Score</div>
                    <Tag value={lender.performance_grade || 'B'} 
                         severity={getPerformanceGradeSeverity(lender.performance_grade || 'B')} 
                         className="mt-2" />
                </Card>
            </div>
            <div className="col-12 md:col-8">
                <Card title="Performance Breakdown">
                    <div className="grid">
                        <div className="col-6">
                            <div className="text-center p-2">
                                <div className="text-2xl font-bold text-green-500">{lender.approval_rate_percentage || 0}%</div>
                                <div className="text-500">Approval Rate</div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="text-center p-2">
                                <div className="text-2xl font-bold text-blue-500">{lender.disbursement_tat_hours || 0}h</div>
                                <div className="text-500">Disbursement Time</div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="text-center p-2">
                                <div className="text-2xl font-bold text-red-500">{lender.default_rate_percentage || 0}%</div>
                                <div className="text-500">Default Rate</div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="text-center p-2">
                                <div className="text-2xl font-bold text-purple-500">{lender.customer_satisfaction_score || 0}/5</div>
                                <div className="text-500">Satisfaction</div>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

const LenderProducts = ({ lender }) => (
    <div className="grid">
        <div className="col-12">
            <Card title="Associated Loan Products">
                <div className="text-center p-4">
                    <i className="pi pi-shopping-cart text-6xl text-blue-200"></i>
                    <div className="text-xl font-semibold mt-3">
                        {lender.active_products_count || 0} Active Products
                    </div>
                    <div className="text-500 mt-2">
                        Products offered by this lender will be shown here
                    </div>
                    <Button 
                        label="View Products" 
                        icon="pi pi-external-link" 
                        className="p-button-outlined mt-3"
                        onClick={() => {
                            // Navigate to products page with lender filter
                            window.open(`/app/loans/products?lender=${lender.lender_code}`, '_blank');
                        }}
                    />
                </div>
            </Card>
        </div>
        <div className="col-12">
            <Panel header="Loan Type Support">
                <div className="flex flex-wrap gap-2">
                    {(lender.supported_loan_types || []).map((type, index) => (
                        <Chip key={index} label={type.replace('_', ' ').toUpperCase()} className="mr-2 mb-2" />
                    )) || <span className="text-500">No loan types configured</span>}
                </div>
            </Panel>
        </div>
    </div>
);

const APIIntegration = ({ lender }) => (
    <div className="grid">
        <div className="col-12 md:col-6">
            <Card title="API Configuration">
                <div className="field">
                    <label>API Endpoint:</label>
                    <div className="font-mono text-sm">{lender.api_endpoint || 'Not configured'}</div>
                </div>
                <div className="field">
                    <label>Authentication:</label>
                    <div>{lender.authentication_method || 'Not specified'}</div>
                </div>
                <div className="field">
                    <label>Data Format:</label>
                    <div className="font-bold">{lender.data_format?.toUpperCase() || 'JSON'}</div>
                </div>
                <div className="field">
                    <label>Integration Status:</label>
                    <Tag value={lender.integration_status} 
                         severity={lender.integration_status === 'active' ? 'success' : 'warning'} />
                </div>
            </Card>
        </div>
        <div className="col-12 md:col-6">
            <Card title="Performance SLA">
                <div className="field">
                    <label>Response Time SLA:</label>
                    <div className="font-bold">{lender.response_time_sla || 5000} ms</div>
                </div>
                <div className="field">
                    <label>API Uptime:</label>
                    <div className="flex align-items-center gap-2">
                        <ProgressBar value={lender.api_uptime_percentage || 0} style={{ width: '100px' }}  aria-label="Progress" />
                        <span className="font-bold">{lender.api_uptime_percentage || 0}%</span>
                    </div>
                </div>
                <div className="field">
                    <label>Supported Operations:</label>
                    <div className="flex flex-wrap gap-1">
                        {(lender.supported_operations || []).map((op, index) => (
                            <Badge key={index} value={op} severity="info" />
                        )) || <span className="text-500">None configured</span>}
                    </div>
                </div>
            </Card>
        </div>
    </div>
);

const PerformanceDashboard = ({ data }) => (
    <div className="grid">
        <div className="col-12">
            <Card title="Performance Overview">
                <div className="text-center">
                    <div className="text-4xl font-bold text-blue-500">{data.overall_score || 75}/100</div>
                    <div className="text-500 mt-2">Overall Performance Score</div>
                    <Tag value={data.grade || 'B'} 
                         severity={getPerformanceGradeSeverity(data.grade || 'B')} 
                         className="mt-2 text-lg" />
                </div>
            </Card>
        </div>
    </div>
);

export default Lenders;